/**
 * Surveys API results content for `/results/api-overview/:extSurveyId`.
 * Shell / top nav come from ResultsPage (match overview: no back button, no breadcrumb).
 * PDF Results layout; Download / Share / AI summary / focus group / markets omitted.
 */
import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  PortalDsAlert,
  PortalDsAnswerBar,
  PortalDsBadge,
  PortalDsCard,
  PortalDsEmptyState,
  PortalDsSegmented,
  PortalDsSelect,
  portalDs,
} from 'src/components/portal-design-system';
import { useSetPageTitle } from 'src/hooks/useSetPageTitle';
import { Skeleton } from 'src/components/ui/skeleton';
import { cn } from 'src/lib/utils';
import {
  adaptResultsToViewModel,
  formatSurveyStatusLabel,
} from 'src/redux/survey/surveys-api-v1/surveys-api-v1.adapters';
import {
  useGetSurveysApiV1ResultsQuery,
  useGetSurveysApiV1SurveyQuery,
  useGetSurveysApiV1VerbatimsQuery,
} from 'src/redux/survey/surveys-api-v1/surveys-api-v1.endpoints';
import type {
  SurveysApiRespondentScope,
  SurveysApiSourceFilter,
} from 'src/redux/survey/surveys-api-v1/surveys-api-v1.types';
import { useSurveysBetaExtClientId } from 'src/pages/surveys/beta/hooks/use-surveys-beta-ext-client-id';
import { surveyStatusPillVariant } from 'src/pages/surveys/beta/utils/survey-status-pill';
import { surveysBetaListPath } from 'src/pages/surveys/beta/utils/surveys-beta-navigation';

type ResultsTab = 'summary' | 'responses';

function parseRespondentScope(value: string | null): SurveysApiRespondentScope {
  if (value === 'ai' || value === 'human') {
    return value;
  }
  return 'all';
}

function parseSource(value: string | null): SurveysApiSourceFilter {
  if (value === 'human' || value === 'ai' || value === 'rtr') {
    return value;
  }
  return 'all';
}

function questionTypeLabel(type: string): string {
  switch (type) {
    case 'multi_select':
      return 'Multi-select';
    case 'free_form':
      return 'Freeform';
    default:
      return 'Single-select';
  }
}

function formatRelative(value: string | null): string {
  if (!value) {
    return 'no responses yet';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'last response unknown';
  }
  return `last response ${date.toLocaleString()}`;
}

const ApiOverviewPage = () => {
  useSetPageTitle('Survey results');
  const navigate = useNavigate();
  const { clientId, extSurveyId = '' } = useParams<{
    clientId?: string;
    extSurveyId?: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const extClientId = useSurveysBetaExtClientId();

  const respondentScope = parseRespondentScope(searchParams.get('respondents'));
  const source = parseSource(searchParams.get('source'));
  const breakout = searchParams.get('breakout');
  const activeTab = (searchParams.get('view') === 'responses'
    ? 'responses'
    : 'summary') as ResultsTab;
  const activeQuestionId = searchParams.get('question');

  const {
    data: survey,
    isLoading: isLoadingSurvey,
    isError: surveyError,
  } = useGetSurveysApiV1SurveyQuery(
    { extSurveyId, extClientId },
    { skip: !extSurveyId }
  );

  const resultsArg = useMemo(
    () => ({
      extSurveyId,
      extClientId,
      source,
      respondentScope,
      breakout: breakout || undefined,
    }),
    [extSurveyId, extClientId, source, respondentScope, breakout]
  );

  const {
    data: results,
    isLoading: isLoadingResults,
    isError: resultsError,
  } = useGetSurveysApiV1ResultsQuery(resultsArg, { skip: !extSurveyId });

  const {
    data: verbatims,
    isFetching: isLoadingVerbatims,
  } = useGetSurveysApiV1VerbatimsQuery(resultsArg, {
    skip: !extSurveyId || activeTab !== 'responses',
  });

  const viewModel = useMemo(() => {
    if (!results) {
      return null;
    }
    return adaptResultsToViewModel(results, {
      respondentScope,
      definitionTitle: survey?.title,
    });
  }, [results, respondentScope, survey?.title]);

  const patchParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value == null || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    setSearchParams(next, { replace: true });
  };

  const isLoading = Boolean(extSurveyId) && (isLoadingSurvey || isLoadingResults);
  const fieldwork = viewModel?.fieldwork;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className={portalDs.sheetTitle}>
            {survey?.title || viewModel?.title || 'Survey results'}
          </h1>
          {survey ? (
            <PortalDsBadge
              pill
              variant={surveyStatusPillVariant(survey.survey_status)}
            >
              {formatSurveyStatusLabel(survey.survey_status)}
            </PortalDsBadge>
          ) : null}
        </div>
        <p className={portalDs.sheetDescription}>
          {(survey?.questions?.length ?? viewModel?.questions.length ?? 0)}
          {' '}
          questions
          {' · '}
          {formatRelative(viewModel?.lastAnswerAt ?? null)}
        </p>
      </div>

      <PortalDsAlert
        variant="info"
        title="Surveys API overview"
      >
        Data from
        {' '}
        <code className="font-mono text-xs">
          /api/v1/surveys/…/results
        </code>
        {'. '}
        Download, share, and AI summary are not wired here yet.
      </PortalDsAlert>

      {(surveyError || resultsError) && (
        <PortalDsAlert
          variant="danger"
          title="Could not load results"
        >
          Check Surveys API entitlements for survey_definitions and survey_results.
        </PortalDsAlert>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1.5">
          <span className={portalDs.filterLabel}>
            Break out by
          </span>
          <PortalDsSelect
            value={breakout || ''}
            onChange={(event) =>
              patchParams({ breakout: event.target.value || null })
            }
          >
            <option value="">
              None
            </option>
            <option value="age_group">
              Age
            </option>
            <option value="gender">
              Gender
            </option>
            <option value="hh_income">
              Household income
            </option>
            <option value="education">
              Education
            </option>
            <option value="ethnicity">
              Ethnicity
            </option>
            <option value="region">
              Region
            </option>
          </PortalDsSelect>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className={portalDs.filterLabel}>
            Responses from
          </span>
          <PortalDsSegmented
            ariaLabel="Respondent scope"
            value={respondentScope}
            options={[
              { value: 'all', label: 'All' },
              { value: 'ai', label: 'AI' },
              { value: 'human', label: 'Human' },
            ]}
            onChange={(value) => patchParams({ respondents: value === 'all' ? null : value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className={portalDs.filterLabel}>
            Source
          </span>
          <PortalDsSegmented
            ariaLabel="Source filter"
            value={source}
            options={[
              { value: 'all', label: 'All' },
              { value: 'ai', label: 'AI' },
              { value: 'human', label: 'Human' },
              { value: 'rtr', label: 'RTR' },
            ]}
            onChange={(value) => patchParams({ source: value === 'all' ? null : value })}
          />
        </div>
      </div>

      <PortalDsSegmented
        ariaLabel="Results view"
        value={activeTab}
        options={[
          { value: 'summary', label: 'Summary' },
          { value: 'responses', label: 'Responses' },
        ]}
        onChange={(value) =>
          patchParams({ view: value === 'summary' ? null : value })
        }
      />

      {isLoading ? (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      ) : null}

      {!isLoading && viewModel ? (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="flex flex-col gap-4">
            {viewModel.suppressionNotes.length > 0 ? (
              <PortalDsAlert
                variant="warning"
                title="Suppression applied"
              >
                {viewModel.suppressionNotes.slice(0, 3).join(' · ')}
              </PortalDsAlert>
            ) : null}

            {activeTab === 'summary' ? (
              viewModel.questions.length === 0 ? (
                <PortalDsEmptyState
                  title="No result questions yet"
                  description="Results will appear once respondents answer this survey."
                />
              ) : (
                viewModel.questions.map((question, index) => (
                  <PortalDsCard
                    key={question.id}
                    id={`question-${question.id}`}
                    className={cn(
                      activeQuestionId === question.id && 'ring-2 ring-[#2563EB]'
                    )}
                  >
                    <p className={portalDs.caption}>
                      {index + 1}
                      {' '}
                      ·
                      {questionTypeLabel(question.type).toUpperCase()}
                      {' '}
                      ·
                      {' '}
                      {question.answered}
                      {' '}
                      of
                      {question.totalRespondents || question.answered}
                      {' '}
                      answered
                    </p>
                    <h2 className={`${portalDs.headingH3} mt-1`}>
                      {question.title}
                    </h2>
                    <div className="mt-4 flex flex-col gap-3">
                      {question.answers.map((answer) => (
                        <PortalDsAnswerBar
                          key={answer.id}
                          label={answer.title}
                          percent={answer.percent}
                          count={answer.count}
                          leading={answer.isLeading}
                        />
                      ))}
                      {question.answers.length === 0 ? (
                        <p className={portalDs.bodySm}>
                          No answer shares for this question (freeform or empty).
                        </p>
                      ) : null}
                    </div>
                    {question.aiIcc != null ? (
                      <p className={`${portalDs.caption} mt-4`}>
                        AI sample · raw N
                        {' '}
                        {question.aiRawN ?? '—'}
                        {' '}
                        · effective N
                        {' '}
                        {question.aiEffectiveN != null
                          ? Math.round(question.aiEffectiveN)
                          : '—'}
                        {' '}
                        · ICC
                        {' '}
                        {Number(question.aiIcc).toFixed(2)}
                      </p>
                    ) : null}
                  </PortalDsCard>
                ))
              )
            ) : (
              <PortalDsCard>
                <p className={portalDs.sectionLabel}>
                  Verbatims
                </p>
                {isLoadingVerbatims ? (
                  <p className={portalDs.bodySm}>
                    Loading responses…
                  </p>
                ) : (verbatims?.verbatims?.length ?? 0) === 0 ? (
                  <PortalDsEmptyState
                    title="No free-text responses"
                    description="Verbatims appear here when respondents leave free-form answers or feedback."
                  />
                ) : (
                  <ul className="mt-3 flex flex-col gap-3">
                    {verbatims!.verbatims.map((row, index) => (
                      <li
                        key={`${index}-${row.answer_text || row.feedback}`}
                        className="rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] px-4 py-3 text-[13.5px] text-[#0D0F1A]"
                      >
                        {row.answer_text || row.feedback || '—'}
                      </li>
                    ))}
                  </ul>
                )}
              </PortalDsCard>
            )}
          </div>

          <aside className="flex flex-col gap-4">
            <PortalDsCard>
              <p className={portalDs.sectionLabel}>
                Fieldwork
              </p>
              <p className="mt-2 text-2xl font-bold tracking-[-0.5px] text-[#0D0F1A]">
                {fieldwork?.completes ?? 0}
                {' '}
                <span className="text-sm font-semibold text-[#5E6278]">
                  completes
                </span>
              </p>
              <p className={portalDs.bodySm}>
                of
                {' '}
                {fieldwork?.starts ?? 0}
                {' '}
                starts
                {fieldwork?.completionRate != null
                  ? ` · ${Math.round(fieldwork.completionRate * 100)}% completion`
                  : ''}
              </p>
            </PortalDsCard>

            <PortalDsCard>
              <p className={portalDs.sectionLabel}>
                Where responses came from
              </p>
              <ul className="mt-3 flex flex-col gap-2 text-[13.5px] text-[#0D0F1A]">
                <li className="flex justify-between">
                  <span>
                    AI panel
                  </span>
                  <span className="font-mono">
                    {fieldwork?.aiCompleted ?? 0}
                  </span>
                </li>
                <li className="flex justify-between">
                  <span>
                    Human panel
                  </span>
                  <span className="font-mono">
                    {fieldwork?.humanCompleted ?? 0}
                  </span>
                </li>
              </ul>
            </PortalDsCard>

            {(fieldwork?.rawN != null || fieldwork?.icc != null) && (
              <PortalDsCard>
                <p className={portalDs.sectionLabel}>
                  Sample quality
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="font-mono text-lg font-semibold text-[#0D0F1A]">
                      {fieldwork.rawN ?? '—'}
                    </p>
                    <p className={portalDs.caption}>
                      Raw N
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-lg font-semibold text-[#0D0F1A]">
                      {fieldwork.effectiveN != null
                        ? Math.round(fieldwork.effectiveN)
                        : '—'}
                    </p>
                    <p className={portalDs.caption}>
                      Effective N
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-lg font-semibold text-[#0D0F1A]">
                      {fieldwork.icc != null ? Number(fieldwork.icc).toFixed(2) : '—'}
                    </p>
                    <p className={portalDs.caption}>
                      ICC
                    </p>
                  </div>
                </div>
              </PortalDsCard>
            )}

            <PortalDsCard>
              <p className={portalDs.sectionLabel}>
                Jump to question
              </p>
              <ul className="mt-3 flex flex-col gap-2">
                {viewModel.questions.map((question, index) => {
                  const leading = question.answers[0];
                  return (
                    <button
                      key={question.id}
                      type="button"
                      className="rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-[#F0F1F5]"
                      onClick={() => {
                        patchParams({ question: question.id, view: null });
                        document
                          .getElementById(`question-${question.id}`)
                          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                    >
                      <p className="text-[13px] font-semibold text-[#0D0F1A]">
                        {index + 1}
                        {' '}
                        {question.title}
                      </p>
                      {leading ? (
                        <p className={portalDs.caption}>
                          {leading.title}
                          {' '}
                          —
                          {leading.percent.toFixed(1)}
                          %
                        </p>
                      ) : null}
                    </button>
                  );
                })}
              </ul>
            </PortalDsCard>

            <PortalDsCard>
              <p className={portalDs.sectionLabel}>
                Demographics
              </p>
              <p className={`${portalDs.bodySm} mt-2`}>
                {breakout
                  ? `Breakout filter: ${breakout}. Open a question for segment detail via the breakout API in a follow-up.`
                  : 'Choose a breakout attribute above to filter results.'}
              </p>
              <button
                type="button"
                className={`${portalDs.link} mt-3`}
                onClick={() => navigate(surveysBetaListPath(clientId))}
              >
                Back to Surveys (Beta)
              </button>
            </PortalDsCard>
          </aside>
        </div>
      ) : null}
    </div>
  );
};

export default ApiOverviewPage;
